/**
 * Safely converts various date formats (Firestore Timestamp, Date, string, number) to a JavaScript Date object.
 * @param dateValue - The value to convert
 * @returns JavaScript Date object or undefined if conversion fails
 */
export function safeToDate(dateValue: any): Date | undefined {
  if (!dateValue) {
    return undefined;
  }

  // Already a Date object
  if (dateValue instanceof Date) {
    return dateValue;
  }

  // Firestore Timestamp with toDate method
  if (typeof dateValue.toDate === 'function') {
    try {
      return dateValue.toDate();
    } catch (e) {
      return undefined;
    }
  }

  // Serialized Firestore Timestamp object {seconds: number, nanoseconds: number}
  // or { _seconds: number, _nanoseconds: number }
  if (typeof dateValue === 'object') {
    const seconds = dateValue.seconds ?? dateValue._seconds;
    if (typeof seconds === 'number') {
      return new Date(seconds * 1000);
    }
  }

  // String or number timestamp/ISO string
  if (typeof dateValue === 'string' || typeof dateValue === 'number') {
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? undefined : date;
  }

  return undefined;
}

/**
 * Safely converts various date formats to a JavaScript Date object with fallback
 */
export function safeToDateWithFallback(
  dateValue: any,
  fallback: Date = new Date()
): Date {
  return safeToDate(dateValue) || fallback;
}

/**
 * Safely converts various date formats to an ISO string
 */
export function safeToISOString(dateValue: any, fallback?: string): string {
  const date = safeToDate(dateValue);
  if (date) {
    return date.toISOString();
  }
  return fallback ?? new Date().toISOString();
}
